const STORAGE_KEY = "cea:student-identity-map:v2";
const STORAGE_EVENT = "cea:student-identity-map:changed";
const TRANSFER_KIND = "cea.student-mask-transfer";
const TRANSFER_VERSION = 1;
const TRANSFER_ENCODING = "base64-json";
const TRANSFER_FILE_EXTENSION = ".ceam";

function hasStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function normalizeSchoolId(schoolId) {
  const parsed = Number.parseInt(String(schoolId ?? "0"), 10);
  return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : "";
}

function normalizeIdentifier(identifier) {
  return String(identifier ?? "").trim();
}

function normalizeMaskedName(maskedName) {
  return String(maskedName ?? "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeStudentsMap(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const result = {};

  for (const [identifier, maskedName] of Object.entries(value)) {
    const normalizedIdentifier = normalizeIdentifier(identifier);
    const normalizedMaskedName = normalizeMaskedName(maskedName);

    if (!normalizedIdentifier || !normalizedMaskedName) {
      continue;
    }

    result[normalizedIdentifier] = normalizedMaskedName;
  }

  return result;
}

function buildBase64FromUtf8(value) {
  const normalizedValue = String(value ?? "");

  if (typeof TextEncoder !== "undefined" && typeof btoa === "function") {
    const bytes = new TextEncoder().encode(normalizedValue);
    let binary = "";

    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }

    return btoa(binary);
  }

  return btoa(unescape(encodeURIComponent(normalizedValue)));
}

function parseUtf8FromBase64(value) {
  const normalizedValue = String(value ?? "").trim();
  if (!normalizedValue) {
    throw new Error("Plik masek nie zawiera danych do importu.");
  }

  const binary = atob(normalizedValue);

  if (typeof TextDecoder !== "undefined") {
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }

  return decodeURIComponent(escape(binary));
}

function buildTransferFileName(schoolId, exportedAt = new Date().toISOString()) {
  const normalizedSchoolId = normalizeSchoolId(schoolId) || "school";
  const normalizedDate = String(exportedAt ?? "")
    .slice(0, 10)
    .replaceAll("-", "");
  const safeDate = normalizedDate || new Date().toISOString().slice(0, 10).replaceAll("-", "");

  return `${normalizedSchoolId}-${safeDate}${TRANSFER_FILE_EXTENSION}`;
}

function buildTransferEnvelope(schoolId, students, exportedAt = new Date().toISOString()) {
  const normalizedSchoolId = normalizeSchoolId(schoolId);
  const normalizedStudents = normalizeStudentsMap(students);

  if (!normalizedSchoolId) {
    throw new Error("Nie udalo sie przygotowac eksportu masek dla tej szkoly.");
  }

  if (Object.keys(normalizedStudents).length === 0) {
    throw new Error("Brak lokalnych masek do eksportu.");
  }

  const payload = buildBase64FromUtf8(
    JSON.stringify({
      students: normalizedStudents,
    }),
  );

  return {
    kind: TRANSFER_KIND,
    version: TRANSFER_VERSION,
    schoolId: normalizedSchoolId,
    exportedAt,
    entryCount: Object.keys(normalizedStudents).length,
    payloadEncoding: TRANSFER_ENCODING,
    payload,
  };
}

function parseTransferEnvelope(rawContent) {
  let envelope;

  try {
    envelope = JSON.parse(String(rawContent ?? ""));
  } catch (_error) {
    throw new Error("Plik masek ma nieprawidlowy format.");
  }

  if (!envelope || typeof envelope !== "object" || Array.isArray(envelope)) {
    throw new Error("Plik masek ma nieprawidlowy format.");
  }

  if (String(envelope.kind ?? "") !== TRANSFER_KIND) {
    throw new Error("Wybrany plik nie jest plikiem eksportu masek uczniow.");
  }

  if (Number.parseInt(String(envelope.version ?? "0"), 10) !== TRANSFER_VERSION) {
    throw new Error("Ta wersja pliku masek nie jest obslugiwana.");
  }

  if (String(envelope.payloadEncoding ?? "") !== TRANSFER_ENCODING) {
    throw new Error("Plik masek ma nieobslugiwany sposob kodowania danych.");
  }

  const schoolId = normalizeSchoolId(envelope.schoolId);
  if (!schoolId) {
    throw new Error("Plik masek nie zawiera poprawnego identyfikatora szkoly.");
  }

  let payload;

  try {
    payload = JSON.parse(parseUtf8FromBase64(envelope.payload));
  } catch (_error) {
    throw new Error("Nie udalo sie odczytac danych z pliku masek.");
  }

  const students = normalizeStudentsMap(payload?.students ?? payload);
  if (Object.keys(students).length === 0) {
    throw new Error("Plik masek nie zawiera zadnych poprawnych par identyfikator -> maska.");
  }

  return {
    schoolId,
    students,
    exportedAt: typeof envelope.exportedAt === "string" ? envelope.exportedAt : null,
    entryCount: Object.keys(students).length,
  };
}

function updateSchoolStudents(store, schoolId, students, updatedAt = new Date().toISOString()) {
  const normalizedSchoolId = normalizeSchoolId(schoolId);
  if (!normalizedSchoolId) {
    return store;
  }

  const normalizedStudents = normalizeStudentsMap(students);

  if (Object.keys(normalizedStudents).length > 0) {
    store[normalizedSchoolId] = {
      students: normalizedStudents,
      updatedAt,
    };
  } else {
    delete store[normalizedSchoolId];
  }

  return store;
}

function normalizeSchoolEntry(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {
      students: {},
      updatedAt: null,
    };
  }

  const hasExplicitStudents = value.students && typeof value.students === "object" && !Array.isArray(value.students);
  const students = hasExplicitStudents ? normalizeStudentsMap(value.students) : normalizeStudentsMap(value);

  const updatedAt =
    typeof value.updatedAt === "string" && value.updatedAt.trim() !== "" ? value.updatedAt.trim() : null;

  return {
    students,
    updatedAt,
  };
}

function normalizeStore(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const normalized = {};

  for (const [schoolId, schoolEntry] of Object.entries(value)) {
    const normalizedSchoolId = normalizeSchoolId(schoolId);
    if (!normalizedSchoolId) {
      continue;
    }

    const normalizedEntry = normalizeSchoolEntry(schoolEntry);
    if (Object.keys(normalizedEntry.students).length === 0) {
      continue;
    }

    normalized[normalizedSchoolId] = normalizedEntry;
  }

  return normalized;
}

function tryReadStorage(storageKey) {
  const raw = window.localStorage.getItem(storageKey);
  if (!raw) {
    return null;
  }

  const parsed = JSON.parse(raw);
  return normalizeStore(parsed);
}

function readStore() {
  if (!hasStorage()) {
    return {};
  }

  try {
    const currentStore = tryReadStorage(STORAGE_KEY);
    if (currentStore !== null) {
      return currentStore;
    }

    return {};
  } catch (_error) {
    return {};
  }
}

function writeStore(store) {
  if (!hasStorage()) {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function dispatchChange(detail) {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(STORAGE_EVENT, { detail }));
}

export function createStudentIdentityResolver(store = readStore()) {
  const normalizedStore = normalizeStore(store);
  const schoolEntriesCache = new Map();

  return {
    getEntriesForSchool(schoolId) {
      const normalizedSchoolId = normalizeSchoolId(schoolId);
      if (!normalizedSchoolId) {
        return {};
      }

      if (!schoolEntriesCache.has(normalizedSchoolId)) {
        const schoolEntry = normalizedStore[normalizedSchoolId];
        schoolEntriesCache.set(
          normalizedSchoolId,
          schoolEntry?.students && typeof schoolEntry.students === "object" ? schoolEntry.students : {},
        );
      }

      return schoolEntriesCache.get(normalizedSchoolId) ?? {};
    },

    getMaskedName(schoolId, identifier) {
      const normalizedIdentifier = normalizeIdentifier(identifier);
      if (!normalizedIdentifier) {
        return "";
      }

      const entries = this.getEntriesForSchool(schoolId);
      return normalizeMaskedName(entries[normalizedIdentifier] ?? "");
    },

    getResolvedLabel(schoolId, identifier) {
      const normalizedIdentifier = normalizeIdentifier(identifier);
      if (!normalizedIdentifier) {
        return "";
      }

      return this.getMaskedName(schoolId, normalizedIdentifier) || normalizedIdentifier;
    },

    exportStore() {
      return normalizedStore;
    },
  };
}

export function getStudentIdentityStorageKey() {
  return STORAGE_KEY;
}

export function getStudentIdentityStorageEvent() {
  return STORAGE_EVENT;
}

export function getStudentIdentityEntriesForSchool(schoolId) {
  return createStudentIdentityResolver().getEntriesForSchool(schoolId);
}

export function getStudentMaskedName(schoolId, identifier) {
  return createStudentIdentityResolver().getMaskedName(schoolId, identifier);
}

export function getResolvedStudentLabel(schoolId, identifier) {
  return createStudentIdentityResolver().getResolvedLabel(schoolId, identifier);
}

export function setStudentMaskedName(schoolId, identifier, maskedName) {
  const normalizedSchoolId = normalizeSchoolId(schoolId);
  const normalizedIdentifier = normalizeIdentifier(identifier);
  const normalizedMaskedName = normalizeMaskedName(maskedName);

  if (!normalizedSchoolId || !normalizedIdentifier) {
    return "";
  }

  const store = readStore();
  const schoolEntry = normalizeSchoolEntry(store[normalizedSchoolId] ?? {});
  const schoolStudents = {
    ...schoolEntry.students,
  };

  if (normalizedMaskedName) {
    schoolStudents[normalizedIdentifier] = normalizedMaskedName;
  } else {
    delete schoolStudents[normalizedIdentifier];
  }

  updateSchoolStudents(store, normalizedSchoolId, schoolStudents);

  writeStore(store);
  dispatchChange({
    schoolId: normalizedSchoolId,
    identifier: normalizedIdentifier,
    maskedName: normalizedMaskedName,
  });

  return normalizedMaskedName;
}

export function exportStudentIdentityStore() {
  return createStudentIdentityResolver().exportStore();
}

export function exportStudentIdentityTransferForSchool(schoolId) {
  const normalizedSchoolId = normalizeSchoolId(schoolId);
  const schoolStudents = getStudentIdentityEntriesForSchool(normalizedSchoolId);
  const envelope = buildTransferEnvelope(normalizedSchoolId, schoolStudents);

  return {
    schoolId: normalizedSchoolId,
    entryCount: envelope.entryCount,
    fileName: buildTransferFileName(normalizedSchoolId, envelope.exportedAt),
    content: JSON.stringify(envelope, null, 2),
  };
}

export function importStudentIdentityTransferForSchool(schoolId, rawContent) {
  const normalizedSchoolId = normalizeSchoolId(schoolId);
  if (!normalizedSchoolId) {
    throw new Error("Nie udalo sie ustalic szkoly dla importu masek.");
  }

  const parsedTransfer = parseTransferEnvelope(rawContent);
  if (parsedTransfer.schoolId !== normalizedSchoolId) {
    throw new Error("Wybrany plik masek dotyczy innej szkoly.");
  }

  const store = readStore();
  const schoolEntry = normalizeSchoolEntry(store[normalizedSchoolId] ?? {});
  const mergedStudents = {
    ...schoolEntry.students,
    ...parsedTransfer.students,
  };

  updateSchoolStudents(store, normalizedSchoolId, mergedStudents);
  writeStore(store);
  dispatchChange({
    schoolId: normalizedSchoolId,
    action: "transfer-import",
    entryCount: parsedTransfer.entryCount,
  });

  return {
    schoolId: normalizedSchoolId,
    importedCount: parsedTransfer.entryCount,
    totalCount: Object.keys(mergedStudents).length,
    exportedAt: parsedTransfer.exportedAt,
  };
}
